//#region type & interface

export interface SpeechLanguage {
    id?: number
    value: string
    label: string
}

export interface ErrorItem {
    codeErr: string
    status: string
    message: string
}

export interface SttResponse {
    alternatives: {
        transcript: string;
        confidence: number;
    }[];
    resultEndTime: {
        seconds: string;
        nanos: number;
    };
    languageCode: string;
    index: number;
}

export interface SpeechToTextPayload {
    time: string
    order: string
    base64Url: string
    languageCode: string
}

export interface SpeechToTextResponse {
    time: string;
    transcript: string;
    languageCode: string;
    order: string;
}

//#endregion

//#region const

export const errorItems: ErrorItem[] = [
    {
        codeErr: "3",
        status: "INVALID_ARGUMENT",
        message: "The request contains an invalid argument."
    },
    {
        codeErr: "5",
        status: "NOT_FOUND",
        message: "The requested resource was not found."
    },
    {
        codeErr: "7",
        status: "PERMISSION_DENIED",
        message: "The caller does not have permission to execute the specified operation."
    },
    {
        codeErr: "8",
        status: "RESOURCE_EXHAUSTED",
        message: "Some resource has been exhausted, perhaps a per-user quota, or perhaps the entire file system is out of space."
    },
    {
        codeErr: "9",
        status: "FAILED_PRECONDITION",
        message: "The operation was rejected because the system is not in a state required for the operation's execution."
    },
    {
        codeErr: "10",
        status: "ABORTED",
        message: "The operation was aborted, typically due to a concurrency issue such as a sequencer check failure or transaction abort."
    },
    {
        codeErr: "11",
        status: "OUT_OF_RANGE",
        message: "The operation was attempted past the valid range."
    },
    {
        codeErr: "12",
        status: "UNIMPLEMENTED",
        message: "The operation is not implemented or is not supported/enabled in this service."
    },
    {
        codeErr: "13",
        status: "INTERNAL",
        message: "Internal errors. This means that some invariants expected by the underlying system have been broken."
    },
    {
        codeErr: "14",
        status: "UNAVAILABLE",
        message: "The service is currently unavailable."
    },
    {
        codeErr: "16",
        status: "UNAUTHENTICATED",
        message: "The request does not have valid authentication credentials for the operation."
    },
    {
        codeErr: "400",
        status: "BAD_REQUEST",
        message: "The request cannot be fulfilled due to bad syntax."
    },
    {
        codeErr: "401",
        status: "UNAUTHENTICATED",
        message: "The request does not have valid authentication credentials for the operation."
    },
    {
        codeErr: "403",
        status: "FORBIDDEN",
        message: "The request is understood, but it has been refused or access is not allowed."
    },
    {
        codeErr: "404",
        status: "NOT_FOUND",
        message: "The requested resource was not found."
    },
    {
        codeErr: "409",
        status: "CONFLICT",
        message: "The request could not be processed because of conflict in the request."
    },
    {
        codeErr: "429",
        status: "TOO_MANY_REQUESTS",
        message: `The user has sent too many requests in a given amount of time ("rate limiting").`
    },
    {
        codeErr: "500",
        status: "INTERNAL_SERVER_ERROR",
        message: "An unexpected condition was encountered."
    },
    {
        codeErr: "503",
        status: "SERVICE_UNAVAILABLE",
        message: "The server is currently unable to handle the request due to temporary overloading or maintenance."
    },
    {
        codeErr: "504",
        status: "GATEWAY_TIMEOUT",
        message: "The server, while acting as a gateway or proxy, did not receive a timely response from the upstream server."
    }
]

export const speechLanguageList: SpeechLanguage[] = [
    { id: 1, value: 'en-US', label: 'English (US)' }, //
    { id: 2, value: 'en-GB', label: 'English (UK)' },
    { id: 3, value: 'es-ES', label: 'Español (Spanish)' },
    { id: 4, value: 'cmn-Hant-TW', label: '中文' }, //
    { id: 5, value: 'fr-FR', label: 'Français' },
    { id: 6, value: 'ja-JP', label: '日本語' },
    { id: 7, value: 'ko-KR', label: '한국어' },
    { id: 8, value: 'vi-VN', label: 'Tiếng Việt' }, //
    { id: 9, value: 'de-DE', label: 'Deutsch' },
    { id: 10, value: 'hi-IN', label: 'हिन्दी' },
]

export const defaultSpeechLanguage = speechLanguageList[0]

export const enum FileValidation {
    /**檔案格式: 所有 audio 檔案 */
    // FILE_ACCEPT = 'audio/*',
    FILE_ACCEPT = '.mp3, .wav, .flac, .aac, .wma, .ogg, .aiff, .alac, .ape, .m4a, .ac3, .dts, .dsd, .mqa, .opus',
    /**檔案時長: 240 minutes */
    FILE_DURATION = 240,
    /**檔案大小: 1GB */
    FILE_SIZE_LIMIT = 1,
}

/**
 * Validates if the given file has an audio type.
 *
 * @param {File} file - The file to be validated.
 * @return {boolean} Returns true if the file has an audio type, false otherwise.
 */
export const validateFileType = (file: File): boolean => {
    const fileType = file.type
    return fileType.includes("audio") ? true : false
}

/**
 * Validates if the given file size is within the maximum allowed size.
 *
 * @param {File} file - The file to be validated.
 * @return {boolean} Returns true if the file size is within the maximum allowed size, false otherwise.
 */
export const validateFileSize = (file: File): boolean => {
    return file.size <= FileValidation.FILE_SIZE_LIMIT * 1024 * 1024 * 1024;
};

export type UploadFileErrorType = 'fileTypeError' | 'fileSizeError' | 'fileDurationError' | 'requestTimeOutError'

export interface UploadFileError {
    type: UploadFileErrorType
    name: string
    variableType?: any
    variableValue?: any
}

export const uploadFileErrorList: UploadFileError[] = [
    {
        type: 'fileTypeError',
        name: 'Pro_MS_Upload_Msg1',
    },
    {
        type: 'fileSizeError',
        name: 'Pro_MS_Upload_Msg3',
        variableType: 'fileSize',
        variableValue: FileValidation.FILE_SIZE_LIMIT,
    },
    {
        type: 'fileDurationError',
        name: 'Pro_MS_Upload_Msg4',
        variableType: 'fileDuration',
        variableValue: FileValidation.FILE_DURATION,

    },
    {
        type: 'requestTimeOutError',
        name: 'Pro_MS_Upload_Msg5',
    },
]

//#endregion